import React, { useMemo, useState } from "react";

import {
    Button,
    StandardModal,
    StandardModalActionGroup,
    PermissionGrid,
    LearnMoreAboutPermissionButton,
    MoreAboutPermissionsModalContainer,
    MoreAboutPermissionsContent,
    RoleContainer,
    PermissionDescription,
    PermissionList,
    PermissionListItem,
    ModalSmallHeading
} from "src/components";
import * as ROLE from "src/constants/roles";
import * as PERMISSIONS from "src/constants/permissions";

import Permission from "./PermissionType";
import { getDisplaySections } from "./permissionPresentation";
import { ABOUT_USER_PERMISSIONS_MODAL_WIDTH } from "./permissionModalConstants";

function MoreAboutPermissions({ permissions, customPermissionLabels = {}, hiddenPermissionIds = [] }) {
    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const roles = [
        {
            id: ROLE.ACCOUNT_OWNER,
            role: ["Account Owner"],
            description: "The Account Owner is assigned to the person who created the Me Business Account, or received account ownership. Account Owners can manage and make changes to everything on the App & Web. You can only reassign the Account Owner by transferring the account to a new user email.",
            permissionIds: null
        },
        {
            id: ROLE.ADMIN,
            role: ["Admin"],
            description: "Admin permission have the same access as an account owner but cannot transfer or change the account ownership details.",
            permissionIds: null
        },
        {
            id: ROLE.STANDARD,
            role: ["Standard (Employee)", "Standard (Client)"],
            description: "Standard Employee or Client permissions are users that are existing or newly added to the business. These users have yet to be assigned any permissions by an account owner or admin.",
            permissionIds: []
        },
        {
            id: ROLE.APP_MANAGER,
            role: ["App Manager"],
            description: "App Managers and post to the noticeboard to both employees and clients as well as create custom company playlists.",
            permissionIds: [
                PERMISSIONS.NOTICEBOARD_APP,
                PERMISSIONS.CLIENT_NOTICEBOARD,
                PERMISSIONS.SOCIAL_MEDIA_POSTING,
                PERMISSIONS.TEAM_GOALS,
                PERMISSIONS.CHECKLISTS,
                PERMISSIONS.CREATE_BUSINESS_PLAYLISTS
            ]
        },
        {
            id: ROLE.WEB_MANAGER,
            role: ["Web Manager"],
            description: "Web Managers will be able to log into the business account on a Desktop using their login credentials and access select features within your Business Web Dashboard.",
            permissionIds: [
                PERMISSIONS.NOTICEBOARD,
                PERMISSIONS.CLIENT_NOTICEBOARD,
                PERMISSIONS.SOCIAL_MEDIA_LINKING_POSTING,
                PERMISSIONS.MY_QUOTE,
                PERMISSIONS.SURVEYS,
                PERMISSIONS.POLLS,
                PERMISSIONS.STATISTICS,
                PERMISSIONS.BUSINESS_DIRECTORY,
                PERMISSIONS.PERKS,
                PERMISSIONS.DONATIONS,
                PERMISSIONS.DIGITAL_MEMBER_CARDS,
                PERMISSIONS.SALES_AND_PROMOTIONS,
                PERMISSIONS.DOCUMENTS_VIEW,
                PERMISSIONS.BODY_AND_MIND,
                PERMISSIONS.EMAIL_CAMPAIGNS
            ]
        },
        {
            id: ROLE.MARKETING,
            role: ["Marketing"],
            description: "Users with Marketing permissions will have access to a combination of both App and Web features for your business.",
            permissionIds: [
                PERMISSIONS.NOTICEBOARD_APP,
                PERMISSIONS.CLIENT_NOTICEBOARD,
                PERMISSIONS.SOCIAL_MEDIA_POSTING,
                PERMISSIONS.CREATE_BUSINESS_PLAYLISTS,
                PERMISSIONS.NOTICEBOARD,
                PERMISSIONS.SOCIAL_MEDIA_LINKING_POSTING,
                PERMISSIONS.SURVEYS,
                PERMISSIONS.POLLS,
                PERMISSIONS.STATISTICS,
                PERMISSIONS.PERKS,
                PERMISSIONS.DONATIONS,
                PERMISSIONS.DIGITAL_MEMBER_CARDS,
                PERMISSIONS.ADVERTISING,
                PERMISSIONS.SALES_AND_PROMOTIONS,
                PERMISSIONS.DOCUMENTS_VIEW,
                PERMISSIONS.EMAIL_CAMPAIGNS
            ]
        },
        {
            id: ROLE.FINANCE_AND_SALES,
            role: ["Finance & Sales"],
            description: "Users with Finance & Sales permissions will have access to select features within your Business Web Dashboard.",
            permissionIds: [
                PERMISSIONS.SALES_AND_PROMOTIONS,
                PERMISSIONS.DOCUMENTS_VIEW,
                PERMISSIONS.BILLING
            ]
        },
        {
            id: ROLE.MULTIPLE_PERMISSIONS,
            role: ["Multiple Permissions"],
            description: "When a user has been assigned a select individual permissions other than the preset Permission Types listed they will be shown as having 'Multiple Permissions'. This can be a combinations of any permissions available.",
            permissionIds: []
        },
    ];

    const displaySections = useMemo(() => getDisplaySections(permissions || {}, undefined, customPermissionLabels, hiddenPermissionIds), [permissions, customPermissionLabels, hiddenPermissionIds]);

    const getRoleSections = (role) => {
        if (!role.permissionIds) {
            return displaySections;
        }

        return displaySections
            .map(section => ({
                ...section,
                items: section.items.filter(permission => role.permissionIds.includes(permission.id))
            }))
            .filter(section => section.items.length > 0);
    };

    return (
        <>
            <LearnMoreAboutPermissionButton onClick={handleOpen}>
                About User Permissions
            </LearnMoreAboutPermissionButton>
            <StandardModal
                open={open}
                onClose={handleClose}
                widthMode={ABOUT_USER_PERMISSIONS_MODAL_WIDTH}
                title="About User Permissions"
                description="Review each permission type and the app or web features it can access."
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <Button backgroundColor="#171A22" color="#fff" family="Manrope, Helvetica, sans-serif" weight="700"
                            style={{ borderRadius: "7px", fontFamily: "Manrope, Helvetica, sans-serif", boxShadow: "none", height: 39 }}
                            onClick={handleClose}
                        >Close</Button>
                    </StandardModalActionGroup>
                )}
            >
                <MoreAboutPermissionsModalContainer>
                    <MoreAboutPermissionsContent>
                        {
                            roles.map((role, index) => {
                                const roleSections = getRoleSections(role);
                                const leftSections = roleSections.filter(section => section.key === "app" || section.key === "chat");
                                const rightSections = roleSections.filter(section => section.key === "web");

                                return (
                                    <RoleContainer key={index}>
                                        <div>
                                            <Permission role={role.id} />
                                            { role.id === ROLE.STANDARD &&
                                                <span style={{ marginLeft: "25px" }}>
                                                    <Permission role={role.id} isClient={true} />
                                                </span>
                                            }
                                        </div>
                                        <PermissionDescription>
                                            { role.description }
                                        </PermissionDescription>

                                        { role.id === ROLE.STANDARD && <>
                                            <ModalSmallHeading>Client Permissions vs. Employee Permissions: </ModalSmallHeading>
                                            <PermissionDescription>
                                                In your Me Business portal, client permissions are intentionally limited to protect privacy, while employee permissions offer broader access for efficient service management.
                                            </PermissionDescription>
                                        </> }

                                        <PermissionGrid>
                                            <div className="list" style={{ paddingLeft: "10px" }}>
                                                {leftSections.map(section => (
                                                    <div key={section.key}>
                                                        <ModalSmallHeading>{section.title}</ModalSmallHeading>
                                                        <PermissionList>
                                                            {section.items.map(permission => (
                                                                <li key={`${section.key}-${permission.parentId || "root"}-${permission.id}`}>
                                                                    <PermissionListItem depth={permission.depth}>* {permission.name}</PermissionListItem>
                                                                </li>
                                                            ))}
                                                        </PermissionList>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="list">
                                                {rightSections.map(section => (
                                                    <div key={section.key}>
                                                        <ModalSmallHeading>{section.title}</ModalSmallHeading>
                                                        <PermissionList>
                                                            {section.items.map(permission => (
                                                                <li key={`${section.key}-${permission.parentId || "root"}-${permission.id}`}>
                                                                    <PermissionListItem depth={permission.depth}>* {permission.name}</PermissionListItem>
                                                                </li>
                                                            ))}
                                                        </PermissionList>
                                                    </div>
                                                ))}
                                            </div>
                                        </PermissionGrid>
                                    </RoleContainer>
                                );
                            })
                        }
                    </MoreAboutPermissionsContent>
                </MoreAboutPermissionsModalContainer>
            </StandardModal>
        </>
    );
}

export default MoreAboutPermissions;
